import { clamp, isEmpty } from 'lodash';
import { type CSSProperties, Fragment } from 'react';

export interface PopupContentProps {
  header: string;
  headcount?: string;
  growthRate?: string;
  postings?: string;
  timeToFill?: string;
  salaryDist?: string[];
  marketTightness?: string;
  isLoading: boolean;
  height?: number;
  showMarketTightness?: boolean;
}

const HeadcountRow = ({
  headcount,
  isLoading,
}: {
  headcount?: string;
  isLoading: boolean;
}) => {
  if (!headcount) return null;
  return (
    <div className="info-item" data-testid="td-tooltip-headcount">
      <div>Headcount</div>
      <div>
        {isLoading ? '' : headcount}
        <div className="h10" style={{ display: isLoading ? 'flex' : 'none' }}>
          <div className="skeleton skeleton-line br2 skeleton-headcount" />
        </div>
      </div>
    </div>
  );
};

const GrowthRow = ({
  growthRate,
  isLoading,
}: {
  growthRate?: string;
  isLoading: boolean;
}) => {
  if (!growthRate) return null;
  const growthValue = (() => {
    if (growthRate === '-') return growthRate;
    if (growthRate.startsWith('−') || growthRate.startsWith('-')) {
      return <span className="highlight-red">{growthRate}</span>;
    }
    if (growthRate.startsWith('+')) {
      return <span className="highlight-green">{growthRate}</span>;
    }

    return growthRate;
  })();
  return (
    <div className="info-item growth-row" data-testid="td-tooltip-growth">
      <div>Growth</div>
      <div>
        {isLoading ? null : growthValue}
        <div className="h10" style={{ display: isLoading ? 'flex' : 'none' }}>
          <div className="skeleton skeleton-line br2 skeleton-growth" />
        </div>
      </div>
    </div>
  );
};

const PostingsRow = ({
  postings,
  isLoading,
}: {
  postings?: string;
  isLoading: boolean;
}) => {
  if (!postings) return null;
  return (
    <div className="info-item" data-testid="td-tooltip-postings">
      <div>Postings</div>
      <div>
        {isLoading ? '' : postings}
        <div className="h10" style={{ display: isLoading ? 'flex' : 'none' }}>
          <div className="skeleton skeleton-line br2 skeleton-postings" />
        </div>
      </div>
    </div>
  );
};

const TimeToFillRow = ({
  timeToFill,
  isLoading,
}: {
  timeToFill?: string;
  isLoading: boolean;
}) => {
  if (!timeToFill) return null;
  return (
    <div className="info-item">
      <div>Time to Fill</div>
      <div data-testid="td-tooltip-time-to-fill">
        {isLoading ? '' : `${timeToFill} days`}
        <div className="h10" style={{ display: isLoading ? 'flex' : 'none' }}>
          <div className="skeleton skeleton-line br2 skeleton-time-to-fill" />
        </div>
      </div>
    </div>
  );
};

const SalaryDistSection = ({
  salaryDist,
  isLoading,
}: {
  salaryDist?: string[];
  isLoading: boolean;
}) => {
  if (!salaryDist) return null;
  return (
    <div className="plot-container">
      <div className="plot-box plot-box-bell">
        <div className="plot-title">Salary</div>
        <div className="plot-item">
          <div style={{ display: isLoading ? 'none' : 'block' }}>
            {isEmpty(salaryDist) ? (
              <div className="plot-no-data">No data at this time</div>
            ) : (
              <Fragment>
                {/* v1.1 curve — only shown in themed mode; grid strokes ride
                    currentColor so the ink flips per theme. */}
                <svg
                  className="salary-curve-themed"
                  width="100%"
                  height="34"
                  viewBox="0 0 181.865 39.26"
                  preserveAspectRatio="none"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    opacity="0.5"
                    d="M62.0955 18.5264C48.127 30.6494 35.0372 38.6823 9.25862e-05 38.6823H180.273C144.543 38.6823 131.476 25.245 123.728 18.5264C123.728 18.5264 106.268 0.577348 92.5073 0.577348C78.7468 0.577348 62.0955 18.5264 62.0955 18.5264Z"
                    fill="url(#tt-salary-curve-fill)"
                  />
                  <path
                    d="M9.25862e-05 38.6823C35.0372 38.6823 48.127 30.6494 62.0955 18.5264C62.0955 18.5264 78.7468 0.577348 92.5073 0.577348C106.268 0.577348 123.728 18.5264 123.728 18.5264C131.476 25.245 144.543 38.6823 180.273 38.6823"
                    stroke="#63A2D9"
                    strokeWidth="1.1547"
                  />
                  <path
                    d="M9.25862e-05 38.6823H181.865"
                    stroke="currentColor"
                    strokeOpacity="0.1"
                    strokeWidth="1.1547"
                  />
                  <path
                    d="M93.5306 38.6823L93.5306 0.577288"
                    stroke="currentColor"
                    strokeOpacity="0.1"
                    strokeWidth="1.1547"
                  />
                  <path
                    d="M118.933 38.6823L118.933 14.4336"
                    stroke="currentColor"
                    strokeOpacity="0.1"
                    strokeWidth="1.1547"
                  />
                  <path
                    d="M66.9723 38.6823L66.9723 14.4336"
                    stroke="currentColor"
                    strokeOpacity="0.1"
                    strokeWidth="1.1547"
                  />
                  <defs>
                    <linearGradient
                      id="tt-salary-curve-fill"
                      x1="90.1368"
                      y1="0.577348"
                      x2="90.1368"
                      y2="38.6823"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#63A2D9" stopOpacity="0.5" />
                      <stop offset="1" stopColor="#63A2D9" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
                <svg
                  className="salary-curve-legacy"
                  width="100%"
                  height="100%"
                  viewBox="0 0 155 35"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M77.5 1C55.641 1 43.7179 34 0 34H155C109.295 34 97.3718 1 77.5 1Z"
                    fill="url(#paint0_linear_3541_2187)"
                  />
                  <path
                    d="M77 1V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M100 12V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M123 27V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M54 12V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M31 27V34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M155 34L4.84173e-06 34"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="0.5"
                  />
                  <path
                    d="M0 34C43.7179 34 55.641 1 77.5 1C97.3718 1 109.295 34 155 34"
                    stroke="#48AFF4"
                    strokeWidth="2"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_3541_2187"
                      x1="77.5"
                      y1="1"
                      x2="77.5"
                      y2="34"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop stopColor="#48AFF4" stopOpacity="0.5" />
                      <stop offset="1" stopColor="#48AFF4" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="plot-axis">
                  <div
                    className="plot-axis-label"
                    data-testid="td-tooltip-salary-dist"
                  >
                    {salaryDist[0] ?? ''}
                  </div>
                  <div
                    className="plot-axis-label"
                    data-testid="td-tooltip-salary-dist"
                  >
                    {salaryDist[1] ?? ''}
                  </div>
                  <div
                    className="plot-axis-label"
                    data-testid="td-tooltip-salary-dist"
                  >
                    {salaryDist[2] ?? ''}
                  </div>
                </div>
              </Fragment>
            )}
          </div>
          <div
            className="h46"
            style={{ display: isLoading ? 'block' : 'none' }}
          >
            <div className="skeleton skeleton-line br2 skeleton-salary-dist" />
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketTightnessSection = ({
  marketTightness,
  isLoading,
}: {
  marketTightness?: string;
  isLoading: boolean;
}) => {
  if (!marketTightness) return null;

  const parsedMarketTightness =
    marketTightness === '−−' ? 0 : parseInt(marketTightness, 10) || 0;
  const clampedMarketTightness = clamp(parsedMarketTightness, 0, 100);

  return (
    <div className="plot-container" data-testid="td-tooltip-market-tightness">
      <div className="plot-box">
        <div className="plot-title">Market Tightness</div>
        <div className="plot-item">
          <div
            className="slider-popover"
            style={
              {
                left: `${clampedMarketTightness}%`,
                '--tt-popover-shift': `${-clampedMarketTightness}%`,
              } as CSSProperties
            }
          >
            {isLoading ? '' : `${clampedMarketTightness}%`}
          </div>
          <div style={{ display: isLoading ? 'none' : 'block' }}>
            <input
              className="slider"
              type="range"
              min="1"
              max="100"
              value={clampedMarketTightness}
              readOnly
            />
          </div>
          <div
            className="h10full"
            style={{ display: isLoading ? 'block' : 'none' }}
          >
            <div className="skeleton skeleton-line brfull skeleton-market-tightness" />
          </div>
        </div>
      </div>
    </div>
  );
};

export function PopupContent({
  header,
  headcount,
  growthRate,
  postings,
  timeToFill,
  salaryDist,
  marketTightness,
  isLoading,
  height,
  showMarketTightness = false,
}: PopupContentProps) {
  return (
    <div
      className="tooltip-td"
      data-testid="td-tooltip-container"
      style={typeof height === 'number' ? { height } : undefined}
    >
      <div className="header">{header}</div>
      <div className="list-info">
        <HeadcountRow headcount={headcount} isLoading={isLoading} />
        <GrowthRow growthRate={growthRate} isLoading={isLoading} />
        <PostingsRow postings={postings} isLoading={isLoading} />
        <TimeToFillRow timeToFill={timeToFill} isLoading={isLoading} />
        <hr className="divider" />
        {showMarketTightness ? (
          <MarketTightnessSection
            marketTightness={marketTightness}
            isLoading={isLoading}
          />
        ) : null}
        {/* v1.1 separates the two plot sections with a hairline; hidden in
            legacy, which never had it. */}
        {showMarketTightness && marketTightness && salaryDist ? (
          <hr className="divider divider-themed-only" />
        ) : null}
        <SalaryDistSection salaryDist={salaryDist} isLoading={isLoading} />
      </div>
    </div>
  );
}
